import { fmt, STATUS_LABELS, PAYMENT_METHODS } from '../utils';

/* ─── Utility: open document in a new window and print ─── */
export function openPrintWindow(htmlContent) {
  const win = window.open('', '_blank', 'width=900,height=700');
  if (!win) {
    // Fallback: blob URL if popups are blocked
    const blob = new Blob([htmlContent], { type: 'text/html' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.target = '_blank';
    a.click();
    setTimeout(() => URL.revokeObjectURL(url), 5000);
    return;
  }
  win.document.write(htmlContent);
  win.document.close();
  win.focus();
  setTimeout(() => {
    win.print();
    win.close();
  }, 600);
}

const baseStyles = `
  * { box-sizing: border-box; margin: 0; padding: 0; }
  body {
    font-family: 'Segoe UI', Arial, sans-serif;
    font-size: 14px;
    color: #1a1a2e;
    background: white;
    padding: 20px 28px;
  }
  @page { size: A4 portrait; margin: 12mm 14mm 16mm 14mm; }
  @media print { body { padding: 0; } }

  .doc-header {
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    padding-bottom: 20px;
    border-bottom: 3px solid #7C3AED;
    margin-bottom: 24px;
  }
  .doc-logo { height: 80px; }
  .doc-title-block { text-align: right; }
  .doc-type { font-size: 30px; font-weight: 800; color: #7C3AED; letter-spacing: -0.5px; line-height: 1; }
  .doc-number { font-size: 15px; font-weight: 700; color: #4B5563; margin-top: 4px; }
  .doc-status {
    display: inline-block; margin-top: 6px; padding: 3px 12px;
    border-radius: 20px; font-size: 11px; font-weight: 700;
    text-transform: uppercase; letter-spacing: 0.06em;
    background: #EDE9FE; color: #6D28D9;
  }
  .info-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 20px; margin-bottom: 24px; }
  .info-box { background: #F9FAFB; border: 1px solid #E5E7EB; border-radius: 8px; padding: 14px 16px; }
  .info-label { font-size: 10px; font-weight: 700; text-transform: uppercase; letter-spacing: 0.08em; color: #9CA3AF; margin-bottom: 6px; }
  .info-value { font-size: 14px; font-weight: 600; color: #111827; line-height: 1.5; }
  .info-sub { font-size: 13px; color: #6B7280; margin-top: 2px; }

  table { width: 100%; border-collapse: collapse; margin-bottom: 20px; }
  thead tr { background: #7C3AED; color: white; }
  thead th { padding: 10px 12px; font-size: 11px; font-weight: 700; text-transform: uppercase; letter-spacing: 0.06em; text-align: left; }
  thead th.r { text-align: right; }
  tbody tr:nth-child(even) { background: #F5F3FF; }
  tbody td { padding: 10px 12px; font-size: 13px; color: #374151; border-bottom: 1px solid #E5E7EB; }
  tbody td.r { text-align: right; font-weight: 500; }
  td .sku { font-size: 11px; color: #9CA3AF; }

  .totals-block { display: flex; justify-content: flex-end; margin-bottom: 24px; }
  .totals-table { min-width: 280px; border: 1px solid #E5E7EB; border-radius: 8px; overflow: hidden; }
  .t-row { display: flex; justify-content: space-between; padding: 9px 16px; font-size: 13px; border-bottom: 1px solid #F3F4F6; }
  .t-row:last-child { border-bottom: none; }
  .t-row.grand { background: #7C3AED; color: white; font-size: 16px; font-weight: 800; }
  .t-row.paid { color: #059669; font-weight: 600; }
  .t-row.bal { color: #DC2626; font-weight: 700; font-size: 15px; }

  .note-box { background: #F9FAFB; border: 1px solid #E5E7EB; border-radius: 8px; padding: 14px 16px; margin-bottom: 16px; }
  .note-title { font-size: 10px; font-weight: 700; text-transform: uppercase; letter-spacing: 0.08em; color: #9CA3AF; margin-bottom: 6px; }
  .note-text { font-size: 12px; color: #6B7280; line-height: 1.7; white-space: pre-line; }

  .footer { border-top: 1px solid #E5E7EB; padding-top: 14px; margin-top: 8px; display: flex; justify-content: space-between; font-size: 11px; color: #9CA3AF; }
  .footer-brand { font-weight: 700; color: #7C3AED; font-size: 12px; }
`;

function buildHead(title) {
  return `<!DOCTYPE html><html><head><meta charset="utf-8">
    <title>${title}</title>
    <style>${baseStyles}</style>
  </head><body>`;
}

/* ─── QUOTE ─── */
export function printQuote(quote) {
  if (!quote) return;
  const subtotal = quote.subtotal || 0;
  const taxes = quote.taxTotal || 0;
  const total = quote.total || 0;
  const logoUrl = window.location.origin + '/idn-logo.png';

  const items = (quote.items || []).map((item, i) => `
    <tr>
      <td style="color:#9CA3AF">${i + 1}</td>
      <td><strong>${item.description || ''}</strong>${item.code ? `<div class="sku">SKU: ${item.code}</div>` : ''}</td>
      <td class="r">${item.qty}</td>
      <td class="r">${fmt.currency(item.unitPrice)}</td>
      <td class="r">${item.discount || 0}%</td>
      <td class="r">${item.taxRate || 0}%</td>
      <td class="r">${fmt.currency(item.total)}</td>
    </tr>`).join('');

  const html = buildHead(`Cotización ${quote.number}`) + `
    <div class="doc-header">
      <img src="${logoUrl}" class="doc-logo" />
      <div class="doc-title-block">
        <div class="doc-type">COTIZACIÓN</div>
        <div class="doc-number">${quote.number}</div>
        <div class="doc-status">${STATUS_LABELS[quote.status] || quote.status}</div>
      </div>
    </div>

    <div class="info-grid">
      <div class="info-box">
        <div class="info-label">Cliente</div>
        <div class="info-value">${quote.client?.name || '—'}</div>
        ${quote.client?.email ? `<div class="info-sub">${quote.client.email}</div>` : ''}
        ${quote.client?.phone ? `<div class="info-sub">${quote.client.phone}</div>` : ''}
      </div>
      <div class="info-box">
        <div class="info-label">Datos del documento</div>
        <div class="info-sub"><strong>Fecha:</strong> ${fmt.date(quote.createdAt)}</div>
        <div class="info-sub"><strong>Moneda:</strong> ${quote.currency || 'MXN'}</div>
        <div class="info-sub"><strong>Condiciones pago:</strong> ${quote.paymentTerms || '—'}</div>
        ${quote.seller?.name ? `<div class="info-sub"><strong>Vendedor:</strong> ${quote.seller.name}</div>` : ''}
      </div>
    </div>

    <table>
      <thead><tr>
        <th style="width:36px">#</th>
        <th>Descripción</th>
        <th class="r">Cant.</th>
        <th class="r">P. Unit.</th>
        <th class="r">Desc.</th>
        <th class="r">IVA</th>
        <th class="r">Total</th>
      </tr></thead>
      <tbody>${items}</tbody>
    </table>

    <div class="totals-block">
      <div class="totals-table">
        <div class="t-row"><span>Subtotal</span><span>${fmt.currency(subtotal)}</span></div>
        <div class="t-row"><span>IVA</span><span>${fmt.currency(taxes)}</span></div>
        <div class="t-row grand"><span>TOTAL</span><span>${fmt.currency(total)}</span></div>
      </div>
    </div>

    ${quote.publicNote ? `<div class="note-box"><div class="note-title">Notas</div><div class="note-text">${quote.publicNote}</div></div>` : ''}
    ${quote.footerNote ? `<div class="note-box"><div class="note-title">Términos y condiciones</div><div class="note-text">${quote.footerNote}</div></div>` : ''}

    <div class="footer">
      <span class="footer-brand">IDN Innovación</span>
      <span>Documento generado el ${fmt.date(new Date())}</span>
    </div>
  </body></html>`;

  openPrintWindow(html);
}

/* ─── INVOICE ─── */
export function printInvoice(invoice) {
  if (!invoice) return;
  const subtotal = invoice.subtotal || 0;
  const taxes = invoice.taxTotal || 0;
  const total = invoice.total || 0;
  const paid = invoice.paid || 0;
  const balance = invoice.balance ?? (total - paid);
  const logoUrl = window.location.origin + '/idn-logo.png';

  const items = (invoice.items || []).map((item, i) => `
    <tr>
      <td style="color:#9CA3AF">${i + 1}</td>
      <td><strong>${item.description || ''}</strong>${item.code ? `<div class="sku">SKU: ${item.code}</div>` : ''}</td>
      <td class="r">${item.qty}</td>
      <td class="r">${fmt.currency(item.unitPrice)}</td>
      <td class="r">${item.discount || 0}%</td>
      <td class="r">${item.taxRate || 0}%</td>
      <td class="r">${fmt.currency(item.total)}</td>
    </tr>`).join('');

  const html = buildHead(`Factura ${invoice.number}`) + `
    <div class="doc-header">
      <img src="${logoUrl}" class="doc-logo" />
      <div class="doc-title-block">
        <div class="doc-type">FACTURA</div>
        <div class="doc-number">${invoice.number}</div>
        <div class="doc-status">${STATUS_LABELS[invoice.status] || invoice.status}</div>
      </div>
    </div>

    <div class="info-grid">
      <div class="info-box">
        <div class="info-label">Cliente</div>
        <div class="info-value">${invoice.client?.name || '—'}</div>
        ${invoice.client?.email ? `<div class="info-sub">${invoice.client.email}</div>` : ''}
        ${invoice.client?.phone ? `<div class="info-sub">${invoice.client.phone}</div>` : ''}
      </div>
      <div class="info-box">
        <div class="info-label">Datos del documento</div>
        <div class="info-sub"><strong>Fecha:</strong> ${fmt.date(invoice.createdAt)}</div>
        ${invoice.dueDate ? `<div class="info-sub"><strong>Vencimiento:</strong> ${fmt.date(invoice.dueDate)}</div>` : ''}
        <div class="info-sub"><strong>Moneda:</strong> ${invoice.currency || 'MXN'}</div>
        ${invoice.seller?.name ? `<div class="info-sub"><strong>Vendedor:</strong> ${invoice.seller.name}</div>` : ''}
        ${(invoice.quotes||[]).length > 0 ? `<div class="info-sub"><strong>Cotización(es):</strong> ${invoice.quotes.map(q=>q.number||q).join(', ')}</div>` : ''}
      </div>
    </div>

    <table>
      <thead><tr>
        <th style="width:36px">#</th>
        <th>Descripción</th>
        <th class="r">Cant.</th>
        <th class="r">P. Unit.</th>
        <th class="r">Desc.</th>
        <th class="r">IVA</th>
        <th class="r">Total</th>
      </tr></thead>
      <tbody>${items}</tbody>
    </table>

    <div class="totals-block">
      <div class="totals-table">
        <div class="t-row"><span>Subtotal</span><span>${fmt.currency(subtotal)}</span></div>
        <div class="t-row"><span>IVA</span><span>${fmt.currency(taxes)}</span></div>
        <div class="t-row grand"><span>TOTAL</span><span>${fmt.currency(total)}</span></div>
        <div class="t-row paid"><span>Pagado</span><span>− ${fmt.currency(paid)}</span></div>
        <div class="t-row bal"><span>Saldo pendiente</span><span>${fmt.currency(balance)}</span></div>
      </div>
    </div>

    ${invoice.notes ? `<div class="note-box"><div class="note-title">Notas</div><div class="note-text">${invoice.notes}</div></div>` : ''}

    <div class="footer">
      <span class="footer-brand">IDN Innovación</span>
      <span>Documento generado el ${fmt.date(new Date())}</span>
    </div>
  </body></html>`;

  openPrintWindow(html);
}

/* ─── PAYMENT RECEIPT ─── */
export function printPayment(payment) {
  if (!payment) return;
  const logoUrl = window.location.origin + '/idn-logo.png';

  const invoiceRows = (payment.invoices || []).map(inv => `
    <tr>
      <td><strong style="color:#7C3AED">${inv.invoice?.number || '—'}</strong></td>
      <td>${payment.client?.name || '—'}</td>
      <td class="r"><strong>${fmt.currency(inv.amount || payment.amount)}</strong></td>
    </tr>`).join('') || `<tr><td colspan="3" style="text-align:center;color:#9CA3AF">Sin facturas vinculadas</td></tr>`;

  const html = buildHead(`Recibo ${payment.number}`) + `
    <div class="doc-header">
      <img src="${logoUrl}" class="doc-logo" />
      <div class="doc-title-block">
        <div class="doc-type">RECIBO DE PAGO</div>
        <div class="doc-number">${payment.number}</div>
        <div class="doc-status">${payment.status === 'aplicado' ? 'Aplicado' : 'Cancelado'}</div>
      </div>
    </div>

    <div class="info-grid">
      <div class="info-box">
        <div class="info-label">Cliente</div>
        <div class="info-value">${payment.client?.name || '—'}</div>
        ${payment.client?.email ? `<div class="info-sub">${payment.client.email}</div>` : ''}
        ${payment.client?.phone ? `<div class="info-sub">${payment.client.phone}</div>` : ''}
      </div>
      <div class="info-box">
        <div class="info-label">Datos del pago</div>
        <div class="info-sub"><strong>Fecha:</strong> ${fmt.date(payment.date || payment.createdAt)}</div>
        <div class="info-sub"><strong>Método:</strong> ${PAYMENT_METHODS[payment.method] || payment.method}</div>
        ${payment.reference ? `<div class="info-sub"><strong>Referencia:</strong> ${payment.reference}</div>` : ''}
        ${payment.seller?.name ? `<div class="info-sub"><strong>Registrado por:</strong> ${payment.seller.name}</div>` : ''}
      </div>
    </div>

    <p style="font-size:10px;font-weight:700;text-transform:uppercase;letter-spacing:0.08em;color:#9CA3AF;margin-bottom:10px">Facturas aplicadas</p>
    <table>
      <thead><tr>
        <th>Factura</th>
        <th>Cliente</th>
        <th class="r">Monto aplicado</th>
      </tr></thead>
      <tbody>${invoiceRows}</tbody>
    </table>

    <div class="totals-block">
      <div class="totals-table">
        <div class="t-row grand"><span>MONTO TOTAL PAGADO</span><span>${fmt.currency(payment.amount)}</span></div>
      </div>
    </div>

    ${payment.notes ? `<div class="note-box"><div class="note-title">Notas</div><div class="note-text">${payment.notes}</div></div>` : ''}

    <div class="footer">
      <span class="footer-brand">IDN Innovación</span>
      <span>Recibo generado el ${fmt.date(new Date())}</span>
    </div>
  </body></html>`;

  openPrintWindow(html);
}
